<template>
    <BasicHeader pretitle="aqfer Marketing Data Platform" title="Sandbox Credentials" :buttonRoles="allowedUserRoles"
        buttonText="Add Credential" @btnClick="addCredential" buttonClass="btn-primary" />
    <div class="container-fluid">
        <InputField label="Choose a Sandbox" type="select" :options="sandboxOptions" v-model="selectedSandbox"
            class="mb-4 pb-2" />

        <div v-if="loading">
            <div class="card card-body">
                <div class="d-flex justify-content-center align-items-center gap-2">
                    <div class="spinner-border spinner-border-sm" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                    <div class="">Loading credentials...</div>
                </div>
            </div>
        </div>
        <div v-else-if="credentials.length > 0">
            <div class="row">
                <div class="col-3">
                    <div class="card">
                        <div class="card-body p-0">
                            <Datatable :key="'sandbox-aquacred-credentials-' + editMode" ref="credentialsDatatable"
                                tableId="sandboxAquacredCredentials" :columns="credentialColumns" uniqueField="name"
                                :data="credentials" :selectedItem="credentialTableSelectedItem" :lengthChange="false"
                                :info="false" :searching="true" :compact="true" :showLoading="loading" :ignoreCache="true"
                                v-on:selected="handleCredentialTableSelect"/>
                        </div>
                    </div>
                </div>

                <div class="col-9">
                    <div class="card" v-if="currentCredential && Object.keys(currentCredential).length > 0">
                        <div class="card-header d-flex justify-content-between align-items-center">
                            <h4 class="card-header-title">{{ currentCredential.name || "Credential Details" }}</h4>
                            <div class="d-flex gap-2">
                                <button class="btn btn-light" title="Cancel" v-if="editMode && isAccessByUserRole()"
                                    @click.stop="setEditMode(false)">
                                    <i class="fa fa-x"></i>
                                </button>
                                <button class="btn btn-success" title="Save Changes" v-if="editMode && isAccessByUserRole()"
                                    @click.stop="onSave(currentCredential)"
                                    :disabled="action != '' || !validateCredential">
                                    <div v-if="action == 'save'" class="spinner-border spinner-border-sm" role="status"
                                        style="width: 1em; height: 1em;">
                                        <span class="visually-hidden">Loading...</span>
                                    </div>
                                    <i v-else class="fa fa-save"></i>
                                </button>
                                <button class="btn btn-danger" title="Delete" v-if="editMode && isAccessByUserRole()"
                                    @click.stop="onDelete(currentCredential.name)" :disabled="action != ''">
                                    <i class="fa fa-trash-can"></i>
                                </button>
                                <button class="btn btn-primary" title="Edit" v-if="!editMode && isAccessByUserRole()"
                                    @click.stop="setEditMode(true)">
                                    <i class="fa fa-edit"></i>
                                </button>
                            </div>
                        </div>
                        <div class="details-card list-group-flush">
                            <FormFields :fields="credFields" v-model="currentCredential" :inlineField="true"
                                :editMode="editMode" />
                        </div>
                    </div>
                    <div v-else class="text-center py-3 bg-light rounded border border-dashed">
                        <span class="text-muted">
                            <i class="fa fa-info-circle"></i>
                            No credential selected
                        </span>
                    </div>
                </div>
            </div>
        </div>
        <div v-else>
            <div class="card card-body text-center py-4">
                <span class="text-muted">
                    <i class="fa fa-info-circle"></i>
                    No credentials found.
                </span>
            </div>
        </div>
    </div>
</template>

<script>
import BasicHeader from '../../components/BasicHeader.vue';
import InputField from '../../components/InputField.vue';
import FormFields from '../../components/FormFields.vue';
import Datatable from '../../components/Datatable.vue';
import sandboxAquacredCredentials from '../../utils/field-constants/sandboxAquacredCredentials.js';
import { mapActions } from 'vuex';

export default {
    name: 'SandboxAquacredCredentials',
    components: { BasicHeader, InputField, FormFields, Datatable },
    data() {
        return {
            credentials: [],
            currentCredential: null,
            selectedCredential: "", // name of the selected credential
            loading: false,
            action: "",
            editMode: false,
            allowedUserRoles: [1, 1.5, 3], // admins and managers can edit credentials, other can only view
            sandboxCredFields: JSON.parse(JSON.stringify(sandboxAquacredCredentials)),
            badgeColors: {
                aqfer: "badge badge-tomato",
                aws: "badge badge-primary",
                gcp: "badge ",
            },
            requiredFields: {
                aqfer: ['refresh_token'],
                aws: ['access_key', 'secret_key'],
                gcp: ['project_id', 'private_key_id', 'private_key', 'client_email', 'client_id', 'auth_uri', 'token_uri', 'auth_provider_x509_cert_url', 'client_x509_cert_url'],
            },
            credentialColumns: [],
        }
    },
    created() {
        const self = this;
        this.credentialColumns = [
            {
                data: 'name',
                title: 'Credential',
                orderable: true,
                render(data, type, row) {
                    if (type === 'sort' || type === 'filter') return data;
                    const t = self.getCredentialType(row);
                    const badgeClass = self.badgeColors[t] || 'badge';
                    const label = (t || '').toUpperCase();
                    const safeName = String(data || '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
                    return (
                        '<div class="d-flex justify-content-between align-items-center gap-2">' +
                        '<span class="text-truncate" title="' + safeName + '">' + safeName + '</span>' +
                        '<span class="' + badgeClass + ' ms-2 flex-shrink-0">' + label + '</span>' +
                        '</div>'
                    );
                },
            },
        ];
    },
    async beforeMount() {
        await this.fetchCredentials();
    },
    computed: {
        selectedSandbox: {
            get() {
                return this.$store.state.sandbox.currentSandbox;
            },
            set(value) {
                if (value) {
                    this.$store.dispatch('sandbox/setCurrentSandbox', value);
                    this.$router.push({
                        name: "Sandbox Credentials",
                        params: {
                            sandboxId: `${value}`
                        },
                    });
                }
            }
        },
        sandboxOptions() {
            let devSandboxes = this.$store.state.sandbox.devSandboxes ? this.$store.state.sandbox.devSandboxes : [];
            let prodV2Sandboxes = this.$store.state.sandbox.prodV2Sandboxes ? this.$store.state.sandbox.prodV2Sandboxes : [];
            let sandboxes = [...devSandboxes, ...prodV2Sandboxes]
            return sandboxes.map((sandbox) => {
                return { "label": sandbox.name + " - " + sandbox.env, "value": sandbox.name };
            });
        },
        credFields() {
            let fields = this.sandboxCredFields.map((field) => {
                return {
                    ...field,
                    visible: field.filterByType.includes(this.currentCredential.type)
                };
            });
            return fields;
        },
        credentialTableSelectedItem() {
            return this.credentials.find((c) => c.name === this.selectedCredential) || null;
        },
        validateCredential() {
            if (!this.currentCredential || !this.currentCredential.type) return false;
            let requiredFields = this.requiredFields[this.currentCredential.type] || [];
            if (!requiredFields || requiredFields.length === 0) return true;
            return requiredFields.every(field => this.currentCredential[field]);
        },
    },
    methods: {
        ...mapActions('amdp', {
            fetchAquacredCredentials: 'fetchAquacredCredentials',
            deleteAquacredCredentials: 'deleteAquacredCredentials',
            updateAquacredCredential: 'upsertAquacredCredential'
        }),
        isAccessByUserRole() {
            return this.$store.getters.userRoles.isAccessByRole(this.allowedUserRoles);
        },
        setEditMode(mode) {
            this.editMode = mode;
            this.setCurrentCredential(this.selectedCredential);
        },
        getCredentialType(credential) {
            let keys = Object.keys(credential || {});
            if (keys.length > 0) {
                if (keys.includes('refresh_token')) {
                    return "aqfer";
                } else if (keys.includes('access_key') && keys.includes('secret_key')) {
                    return "aws";
                } else {
                    return "gcp";
                }
            }
            return null;
        },
        handleCredentialTableSelect(data) {
            if (data && data.name) {
                this.setCurrentCredential(data.name);
            }
        },
        setCurrentCredential(name) {
            let cred = this.credentials.find((cred) => cred.name === name) || this.credentials?.[0] || null;
            if (cred) {
                this.selectedCredential = cred.name;
                let currentCredential = JSON.parse(JSON.stringify(cred));
                currentCredential.type = this.getCredentialType(currentCredential) || "-";

                // display sensitive information as **********
                let keys = Object.keys(currentCredential || {});
                if (keys.includes('refresh_token') && currentCredential.refresh_token) currentCredential.refresh_token = this.editMode ? "" : "***********";
                if (keys.includes('access_key') && currentCredential.access_key) currentCredential.access_key = this.editMode ? "" : "***********";
                if (keys.includes('secret_key') && currentCredential.secret_key) currentCredential.secret_key = this.editMode ? "" : "***********";
                if (keys.includes('session_token') && currentCredential.session_token) currentCredential.session_token = this.editMode ? "" : "***********";
                if (keys.includes('private_key') && currentCredential.private_key) currentCredential.private_key = this.editMode ? "" : "***********";
                this.currentCredential = JSON.parse(JSON.stringify(currentCredential));
            } else {
                this.currentCredential = JSON.parse(JSON.stringify({}));
            }
        },
        async fetchCredentials() {
            this.loading = true;
            try {
                const response = await this.fetchAquacredCredentials({
                    service: 'amdp',
                    namespace: this.$route.params.sandboxId
                });
                this.credentials = Object.entries(response?.data?.credentials || {}).map(([key, value]) => ({
                    name: key,
                    ...value,
                }));
                this.setCurrentCredential(this.selectedCredential || this.credentials?.[0] || "");
            } catch (error) {
                let err = error?.response?.data?.error || error?.message;
                console.error(err);
                this.$store.dispatch('addToast', {
                    message: "Something went wrong while fetching credentials. Please contact support.",
                    type: 'error'
                });
            } finally {
                this.loading = false;
            }
        },
        async onDelete(cred, action) {
            let self = this;
            this.$store.dispatch('showModal', {
                component: "confirm",
                data: {
                    title: "Delete Credential",
                    message: `Are you sure you want to <strong class="text-danger">DELETE</strong> the credential - <strong>${cred}</strong>?`,
                    confirmText: 'Delete',
                    confirmClass: 'btn-danger',
                    cancelText: 'Cancel',
                    cancelClass: 'btn-secondary',
                    async confirm() {
                        try {
                            await self.deleteAquacredCredentials({
                                service: 'amdp',
                                namespace: self.$route.params.sandboxId,
                                credentialName: cred
                            });
                            self.$store.dispatch('addToast', {
                                message: "Credential deleted successfully.",
                                type: 'success'
                            });
                            self.fetchCredentials(); // refresh credentials list
                        } catch (error) {
                            let err = error?.response?.data?.error || error?.message;
                            console.error(err);
                            self.$store.dispatch('addToast', {
                                type: 'error',
                                message: "Something went wrong while deleting credential. Please contact support.",
                            });
                        }
                    }
                }
            });
        },
        async onSave(cred) {
            this.action = "save";
            try {
                let payload = {
                    name: cred.name,
                    type: cred.type,
                }
                // add only visible fields to payload
                this.credFields.map((field) => {
                    if (field.visible && cred[field.name]) {
                        payload[field.name] = cred[field.name];
                    }
                });
                await this.updateAquacredCredential({
                    service: 'amdp',
                    namespace: this.$route.params.sandboxId,
                    credentialName: cred.name,
                    payload: payload
                });
                this.$store.dispatch('addToast', {
                    message: "Credential updated successfully.",
                    type: 'success'
                });
                this.fetchCredentials(); // refresh credentials list
            } catch (error) {
                let err = error?.response?.data?.error || error?.message;
                console.error(err);
                this.$store.dispatch('addToast', {
                    message: "Something went wrong while updating credential. Please contact support.",
                    type: 'error'
                });
            } finally {
                this.action = "";
            }
        },
        addCredential() {
            this.$store.dispatch('showModal', {
                component: 'AddAquacredCredential',
                data: {
                    sandbox: this.selectedSandbox,
                    refreshCredentials: this.fetchCredentials
                }
            });
        },
    },
}
</script>
<style scoped>
.details-card {
    padding: 0.25rem 1.2rem;
}
</style>